import { FC, SyntheticEvent } from 'react';
import { Autocomplete, Button, InputLabel, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ObjectSchema } from 'yup';
import { LoadingButton } from '@mui/lab';
import { Publish } from '@mui/icons-material';
import { NumericInput } from '../../../../../components/Inputs/NumericInput';
import { FieldContainer, FormikOption, FormGrid } from './StyledFormComponents';

import { KeyResultInitialValues, keyResultInitialValues } from './keyResultInitialValues';
import { keyResultSchema } from './keyResultSchema';
import { ButtonContainer } from './ObjectiveForm';

const statusOptions: FormikOption[] = ['Progress', 'At Risk', 'Completed'].map((status) => ({
  id: status,
  value: status,
  label: status === 'Progress' ? 'In Progress' : status,
}));

const displayTypeOptions: FormikOption[] = [
  { id: 'percentage', value: 'percentage', label: 'Percentage' },
  { id: 'value', value: 'value', label: 'Value' },
];

interface KeyResultFormProps {
  onClose: () => void;
  initialValues?: KeyResultInitialValues;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (values: KeyResultInitialValues) => void | Promise<any>;
  onCancel?: () => void;
}

export const KeyResultForm: FC<KeyResultFormProps> = ({ onClose, onSubmit, onCancel, initialValues }) => {
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<KeyResultInitialValues>({
    defaultValues: initialValues ?? keyResultInitialValues,
    mode: 'all',
    resolver: yupResolver(keyResultSchema as ObjectSchema<KeyResultInitialValues>),
  });

  const displayType = watch('displayType');

  const submit = handleSubmit(async (values) => {
    await onSubmit(values);
  });

  return (
    <FieldContainer>
      <FormGrid>
        <InputLabel required htmlFor={`name`}>
          Name
        </InputLabel>
        <Controller
          name='name'
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              id={`name`}
              name={field.name}
              error={Boolean(fieldState.error && fieldState.isTouched)}
              helperText={fieldState.error && fieldState.isTouched ? fieldState.error.message : ''}
              onBlur={field.onBlur}
              value={field.value}
              onChange={field.onChange}
              placeholder='Enter Name'
            />
          )}
        />
        <InputLabel required htmlFor={`status`}>
          Status
        </InputLabel>
        <Controller
          name='status'
          control={control}
          render={({ field, fieldState }) => (
            <Autocomplete
              options={statusOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name={`status`}
                  id={`status`}
                  error={Boolean(fieldState.error && fieldState.isTouched)}
                  helperText={fieldState.error && fieldState.isTouched ? fieldState.error.message : ''}
                  placeholder='Select Status'
                />
              )}
              onChange={(_e: SyntheticEvent, v: FormikOption | null) => field.onChange(v ? v.value : null)}
              onBlur={field.onBlur}
              value={statusOptions.find((option) => option.id === field.value) ?? null}
              selectOnFocus
            />
          )}
        />
        <InputLabel required htmlFor={`displayType`}>
          Goal Type
        </InputLabel>
        <Controller
          name='displayType'
          control={control}
          render={({ field, fieldState }) => (
            <Autocomplete
              options={displayTypeOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id={`displayType`}
                  name={`displayType`}
                  error={Boolean(fieldState.error && fieldState.isTouched)}
                  helperText={fieldState.error && fieldState.isTouched ? fieldState.error.message : ''}
                  placeholder='Select Goal Type'
                />
              )}
              onChange={(_e: SyntheticEvent, v: FormikOption | null) => field.onChange(v ? v.value : null)}
              onBlur={field.onBlur}
              value={displayTypeOptions.find((option) => option.id === field.value) ?? null}
              selectOnFocus
              blurOnSelect={Boolean(field.value)}
            />
          )}
        />

        <InputLabel htmlFor={`value`}>Current Value</InputLabel>
        <Controller
          name='value'
          control={control}
          render={({ field, fieldState }) => (
            <NumericInput
              id={`value`}
              name={field.name}
              error={Boolean(fieldState.error && fieldState.isTouched)}
              helperText={fieldState.error && fieldState.isTouched ? fieldState.error.message : ''}
              onBlur={field.onBlur}
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
              suffix={displayType === 'percentage' ? '%' : ''}
              prefix={displayType === 'value' ? '$' : ''}
            />
          )}
        />

        <InputLabel required htmlFor={`goal`}>
          Goal Value
        </InputLabel>
        <Controller
          name='goal'
          control={control}
          render={({ field, fieldState }) => (
            <NumericInput
              id={`goal`}
              name={field.name}
              error={Boolean(fieldState.error && fieldState.isTouched)}
              helperText={fieldState.error && fieldState.isTouched ? fieldState.error.message : ''}
              onBlur={field.onBlur}
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
              suffix={displayType === 'percentage' ? '%' : ''}
              prefix={displayType === 'value' ? '$' : ''}
            />
          )}
        />

        <div></div>
        <ButtonContainer>
          <LoadingButton
            variant='contained'
            color={'secondary'}
            size={'large'}
            disabled={!isDirty || Object.keys(errors).length !== 0 || isSubmitting}
            onClick={submit}
            loading={isSubmitting}
            endIcon={<Publish />}
            loadingPosition='end'
          >
            {initialValues ? 'Save Changes' : 'Add Key Result'}
          </LoadingButton>
          <Button
            type='button'
            variant='outlined'
            color='secondary'
            size={'large'}
            onClick={() => {
              reset();
              if (onCancel) {
                onCancel();
              } else {
                onClose();
              }
            }}
          >
            Cancel
          </Button>
        </ButtonContainer>
      </FormGrid>
    </FieldContainer>
  );
};
